import React, { useState, useEffect, useRef } from 'react';
import { useBaseUrl } from '../../../utils/utils';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faVideo, faMicrophone, faPhone, faPhoneSlash, faVideoSlash,
    faMicrophoneSlash, faHeartbeat, faThermometerHalf,
    faCalendarAlt, faComments, faFileMedical, faDownload,
    faUpload, faCog, faUsers, faRecordVinyl, faStop,
    faRefresh, faTimes, faChartBar, faChartPie
} from '@fortawesome/free-solid-svg-icons';

interface TelehealthSession {
    session_id: string;
    collaboration_id: string;
    session_type: 'video' | 'audio' | 'consultation';
    status: 'scheduled' | 'active' | 'completed' | 'cancelled';
    participants: Array<{
        id: number;
        name: string;
        type: string;
        joined: boolean;
        muted: boolean;
        video_off: boolean;
    }>;
    scheduled_time: string;
    duration: number;
    recording_enabled: boolean;
    iot_devices: Array<{
        device_id: string;
        device_type: string;
        status: 'connected' | 'disconnected' | 'error';
        last_reading: any;
    }>;
    notes: string;
}

interface Props {
    collaborationId?: string | null;
    sessionId?: string | null;
    onClose?: () => void;
    onSessionEnd?: () => void;
}

const TelehealthInterface: React.FC<Props> = ({ collaborationId, sessionId, onClose, onSessionEnd }) => {
    const baseUrl = useBaseUrl();
    const [session, setSession] = useState<TelehealthSession | null>(null);
    const [loading, setLoading] = useState(false);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [activeTab, setActiveTab] = useState<'video' | 'audio' | 'iot' | 'notes'>('video');
    const [chatMessage, setChatMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<Array<{
        id: string;
        sender: string;
        message: string;
        timestamp: string;
        type: 'text' | 'file' | 'image';
    }>>([]);
    const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [selectedDeviceType, setSelectedDeviceType] = useState('');
    const [deviceReadings, setDeviceReadings] = useState<{[key: string]: any}>({});

    const localVideoRef = useRef<HTMLVideoElement>(null);
    const remoteVideoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (sessionId) {
            fetchSession();
        }
        initializeMedia();
        return () => {
            if (localStream) {
                localStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [sessionId, baseUrl]);

    const fetchSession = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}collaborations/telehealth-sessions/${sessionId}/`);
            if (response.data.status === 'success') {
                setSession(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching session:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch session details'
            });
        } finally {
            setLoading(false);
        }
    };

    const initializeMedia = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true
            });
            setLocalStream(stream);
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }
        } catch (error) {
            console.error('Error accessing media devices:', error);
            Swal.fire({
                icon: 'warning',
                title: 'Media Access',
                text: 'Unable to access camera/microphone. Please check permissions.'
            });
        }
    };

    const toggleMute = () => {
        if (localStream) {
            const audioTrack = localStream.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMuted(!audioTrack.enabled);
            }
        }
    };

    const toggleVideo = () => {
        if (localStream) {
            const videoTrack = localStream.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setIsVideoOff(!videoTrack.enabled);
            }
        }
    };

    const toggleRecording = () => {
        setIsRecording(!isRecording);
        Swal.fire({
            icon: isRecording ? 'info' : 'success',
            title: isRecording ? 'Recording Stopped' : 'Recording Started',
            text: isRecording ? 'Session recording has been stopped' : 'Session is now being recorded'
        });
    };

    const sendChatMessage = async () => {
        if (!chatMessage.trim()) return;

        const newMessage = {
            id: `msg_${Date.now()}`,
            sender: 'You',
            message: chatMessage,
            timestamp: new Date().toISOString(),
            type: 'text' as const
        };

        setChatMessages(prev => [...prev, newMessage]);
        setChatMessage('');

        try {
            await axios.post(`${baseUrl}collaborations/instant-messages/`, {
                collaboration_id: collaborationId,
                message_type: 'text',
                content: chatMessage,
                sender_type: 'professional'
            });
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const connectIoTDevice = async (deviceType: string) => {
        try {
            const response = await axios.post(`${baseUrl}collaborations/iot-devices/`, {
                collaboration_id: collaborationId,
                device_type: deviceType,
                status: 'connected'
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Device Connected',
                    text: `${deviceType} device has been connected successfully`
                });
                fetchSession();
            }
        } catch (error) {
            console.error('Error connecting device:', error);
            Swal.fire({
                icon: 'error',
                title: 'Connection Failed',
                text: 'Failed to connect IoT device'
            });
        }
    };

    const getStatusBadge = (status: string) => {
        const statusColors = {
            'scheduled': 'bg-info',
            'active': 'bg-success',
            'completed': 'bg-secondary',
            'cancelled': 'bg-danger'
        };
        return statusColors[status as keyof typeof statusColors] || 'bg-secondary';
    };

    const getDeviceIcon = (deviceType: string) => {
        switch (deviceType.toLowerCase()) {
            case 'heart_rate_monitor':
            case 'pulse_oximeter':
                return faHeartbeat;
            case 'blood_pressure_monitor':
            case 'temperature_sensor':
                return faThermometerHalf;
            default:
                return faCog;
        }
    };

    const refreshDeviceReading = async (deviceId: string) => {
        try {
            const response = await axios.post(`${baseUrl}collaborations/iot-devices/${deviceId}/refresh/`);
            if (response.data.status === 'success') {
                setDeviceReadings(prev => ({
                    ...prev,
                    [deviceId]: response.data.data.reading
                }));
                Swal.fire({
                    icon: 'success',
                    title: 'Reading Refreshed',
                    text: 'Device reading has been updated successfully'
                });
            }
        } catch (error) {
            console.error('Error refreshing device reading:', error);
            Swal.fire({
                icon: 'error',
                title: 'Refresh Failed',
                text: 'Failed to refresh device reading'
            });
        }
    };

    const disconnectDevice = async (deviceId: string) => {
        try {
            const response = await axios.post(`${baseUrl}collaborations/iot-devices/${deviceId}/disconnect/`);
            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Device Disconnected',
                    text: 'Device has been disconnected successfully'
                });
                fetchSession();
            }
        } catch (error) {
            console.error('Error disconnecting device:', error);
            Swal.fire({
                icon: 'error',
                title: 'Disconnect Failed',
                text: 'Failed to disconnect device'
            });
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!session) {
        return (
            <div className="text-center py-5">
                <FontAwesomeIcon icon={faVideo} size="3x" className="text-muted mb-3" />
                <h5>No Session Selected</h5>
                <p className="text-muted">Please select a telehealth session to join</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">
                        <FontAwesomeIcon icon={faVideo} className="me-3 text-primary" />
                        Telehealth Session
                    </h3>
                    <div className="d-flex align-items-center gap-3">
                        <span className={`badge ${getStatusBadge(session.status)} fs-6`}>
                            {session.status.toUpperCase()}
                        </span>
                        <span className="text-muted">Session ID: {session.session_id}</span>
                        <span className="text-muted">Type: {session.session_type}</span>
                    </div>
                </div>
                <div>
                    <button className="btn btn-outline-primary me-2">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                        Schedule
                    </button>
                                         {(onClose || onSessionEnd) && (
                         <button 
                             className="btn btn-outline-secondary" 
                             onClick={() => {
                                 if (onSessionEnd) {
                                     onSessionEnd();
                                 } else if (onClose) {
                                     onClose();
                                 }
                             }}
                         >
                             Close
                         </button>
                     )}
                </div>
            </div>

            <div className="row">
                {/* Main Video/Audio Area */}
                <div className="col-lg-8">
                    <div className="card">
                        <div className="card-header">
                            <ul className="nav nav-tabs card-header-tabs">
                                <li className="nav-item">
                                    <button 
                                        className={`nav-link ${activeTab === 'video' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('video')}
                                    >
                                        <FontAwesomeIcon icon={faVideo} className="me-2" />
                                        Video
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button 
                                        className={`nav-link ${activeTab === 'audio' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('audio')}
                                    >
                                        <FontAwesomeIcon icon={faMicrophone} className="me-2" />
                                        Audio
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button 
                                        className={`nav-link ${activeTab === 'iot' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('iot')}
                                    >
                                        <FontAwesomeIcon icon={faHeartbeat} className="me-2" />
                                        IoT Devices
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button 
                                        className={`nav-link ${activeTab === 'notes' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('notes')}
                                    >
                                        <FontAwesomeIcon icon={faFileMedical} className="me-2" />
                                        Notes
                                    </button>
                                </li>
                            </ul>
                        </div>
                        <div className="card-body">
                            {activeTab === 'video' && (
                                <div className="video-container">
                                    <div className="row">
                                        <div className="col-md-8">
                                            <div className="main-video-container">
                                                <video
                                                    ref={remoteVideoRef}
                                                    className="w-100 rounded"
                                                    style={{ height: '400px', objectFit: 'cover' }}
                                                    autoPlay
                                                    playsInline
                                                />
                                                <div className="video-overlay">
                                                    <span className="badge bg-dark">Remote Participant</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="local-video-container">
                                                <video
                                                    ref={localVideoRef}
                                                    className="w-100 rounded"
                                                    style={{ height: '200px', objectFit: 'cover' }}
                                                    autoPlay
                                                    playsInline
                                                    muted
                                                />
                                                <div className="video-overlay">
                                                    <span className="badge bg-primary">You</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'audio' && (
                                <div className="audio-container text-center py-5">
                                    <FontAwesomeIcon icon={faMicrophone} size="5x" className="text-primary mb-3" />
                                    <h5>Audio Conference</h5>
                                    <p className="text-muted">Audio-only session in progress</p>
                                    <div className="d-flex justify-content-center gap-2">
                                        <button 
                                            className={`btn btn-lg ${isMuted ? 'btn-danger' : 'btn-success'}`}
                                            onClick={toggleMute}
                                        >
                                            <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} className="me-2" />
                                            {isMuted ? 'Unmute' : 'Mute'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'iot' && (
                                <div className="iot-container">
                                    {/* View Mode Toggle */}
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">Device View Mode</h6>
                                                <div className="btn-group btn-group-sm">
                                                    <button 
                                                        className={`btn btn-outline-primary ${viewMode === 'cards' ? 'active' : ''}`}
                                                        onClick={() => setViewMode('cards')}
                                                    >
                                                        <FontAwesomeIcon icon={faChartPie} className="me-1" />
                                                        Cards
                                                    </button>
                                                    <button 
                                                        className={`btn btn-outline-primary ${viewMode === 'list' ? 'active' : ''}`}
                                                        onClick={() => setViewMode('list')}
                                                    >
                                                        <FontAwesomeIcon icon={faChartBar} className="me-1" />
                                                        List
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-8">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="mb-0">
                                                        <FontAwesomeIcon icon={faHeartbeat} className="me-2" />
                                                        Connected Devices ({session.iot_devices.length})
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    {viewMode === 'cards' ? (
                                                        <div className="row g-3">
                                                            {session.iot_devices.length > 0 ? (
                                                                session.iot_devices.map((device, index) => (
                                                                    <div key={index} className="col-md-6">
                                                                        <div className="card border-0 shadow-sm h-100">
                                                                            <div className="card-body text-center">
                                                                                <div className="mb-3">
                                                                                    <FontAwesomeIcon 
                                                                                        icon={getDeviceIcon(device.device_type)} 
                                                                                        size="3x" 
                                                                                        className={`text-${device.status === 'connected' ? 'success' : 'warning'}`}
                                                                                    />
                                                                                </div>
                                                                                <h6 className="fw-bold mb-2">{device.device_type.replace('_', ' ').toUpperCase()}</h6>
                                                                                <small className="text-muted d-block mb-2">ID: {device.device_id}</small>
                                                                                <span className={`badge bg-${device.status === 'connected' ? 'success' : 'warning'} mb-3`}>
                                                                                    {device.status}
                                                                                </span>
                                                                                
                                                                                {device.status === 'connected' && device.last_reading && (
                                                                                    <div className="mt-3 p-3 bg-light rounded">
                                                                                        <h6 className="text-primary mb-2">Latest Reading</h6>
                                                                                        <div className="row text-center">
                                                                                            {Object.entries(device.last_reading).map(([key, value]) => (
                                                                                                <div key={key} className="col-6">
                                                                                                    <small className="text-muted d-block">{key.replace('_', ' ')}</small>
                                                                                                    <strong className="text-dark">{String(value)}</strong>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                                
                                                                                <div className="mt-3">
                                                                                    <button 
                                                                                        className="btn btn-sm btn-outline-primary me-2"
                                                                                        onClick={() => refreshDeviceReading(device.device_id)}
                                                                                    >
                                                                                        <FontAwesomeIcon icon={faRefresh} className="me-1" />
                                                                                        Refresh
                                                                                    </button>
                                                                                    <button 
                                                                                        className="btn btn-sm btn-outline-danger"
                                                                                        onClick={() => disconnectDevice(device.device_id)}
                                                                                    >
                                                                                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                                                        Disconnect
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="text-center py-4">
                                                                    <FontAwesomeIcon icon={faHeartbeat} size="3x" className="text-muted mb-3" />
                                                                    <h6>No IoT devices connected</h6>
                                                                    <p className="text-muted">Connect devices to monitor patient vitals</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="table-responsive">
                                                            <table className="table table-hover">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Device</th>
                                                                        <th>Status</th>
                                                                        <th>Last Reading</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {session.iot_devices.map((device, index) => (
                                                                        <tr key={index}>
                                                                            <td>
                                                                                <div className="d-flex align-items-center">
                                                                                    <FontAwesomeIcon 
                                                                                        icon={getDeviceIcon(device.device_type)} 
                                                                                        className={`text-${device.status === 'connected' ? 'success' : 'warning'} me-2`}
                                                                                        size="lg"
                                                                                    />
                                                                                    <div>
                                                                                        <strong>{device.device_type.replace('_', ' ').toUpperCase()}</strong>
                                                                                        <br />
                                                                                        <small className="text-muted">ID: {device.device_id}</small>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge bg-${device.status === 'connected' ? 'success' : 'warning'}`}>
                                                                                    {device.status}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                {device.status === 'connected' && device.last_reading ? (
                                                                                    <div>
                                                                                        {Object.entries(device.last_reading).map(([key, value]) => (
                                                                                            <div key={key}>
                                                                                                <small className="text-muted">{key.replace('_', ' ')}: </small>
                                                                                                <strong>{String(value)}</strong>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted">No data</span>
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                <div className="btn-group btn-group-sm">
                                                                                    <button 
                                                                                        className="btn btn-outline-primary"
                                                                                        onClick={() => refreshDeviceReading(device.device_id)}
                                                                                    >
                                                                                        <FontAwesomeIcon icon={faRefresh} />
                                                                                    </button>
                                                                                    <button 
                                                                                        className="btn btn-outline-danger"
                                                                                        onClick={() => disconnectDevice(device.device_id)}
                                                                                    >
                                                                                        <FontAwesomeIcon icon={faTimes} />
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h6 className="mb-0">
                                                        <FontAwesomeIcon icon={faCog} className="me-2" />
                                                        Connect New Device
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="d-grid gap-2">
                                                        <button 
                                                            className="btn btn-outline-primary"
                                                            onClick={() => connectIoTDevice('heart_rate_monitor')}
                                                        >
                                                            <FontAwesomeIcon icon={faHeartbeat} className="me-2" />
                                                            Heart Rate Monitor
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-primary"
                                                            onClick={() => connectIoTDevice('blood_pressure_monitor')}
                                                        >
                                                            <FontAwesomeIcon icon={faThermometerHalf} className="me-2" />
                                                            Blood Pressure Monitor
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-primary"
                                                            onClick={() => connectIoTDevice('pulse_oximeter')}
                                                        >
                                                            <FontAwesomeIcon icon={faHeartbeat} className="me-2" />
                                                            Pulse Oximeter
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-primary"
                                                            onClick={() => setShowDeviceModal(true)}
                                                        >
                                                            <FontAwesomeIcon icon={faCog} className="me-2" />
                                                            Custom Device
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'notes' && (
                                <div className="notes-container">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Session Notes</label>
                                        <textarea
                                            className="form-control"
                                            rows={8}
                                            placeholder="Enter session notes, observations, or recommendations..."
                                            defaultValue={session.notes}
                                        />
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-primary">
                                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                                            Save Notes
                                        </button>
                                        <button className="btn btn-outline-secondary">
                                            <FontAwesomeIcon icon={faUpload} className="me-2" />
                                            Upload Document
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Control Panel & Chat */}
                <div className="col-lg-4">
                    {/* Control Panel */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faCog} className="me-2" />
                                Controls
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="d-grid gap-2">
                                <button 
                                    className={`btn btn-lg ${isMuted ? 'btn-danger' : 'btn-success'}`}
                                    onClick={toggleMute}
                                >
                                    <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} className="me-2" />
                                    {isMuted ? 'Unmute' : 'Mute'}
                                </button>
                                <button 
                                    className={`btn btn-lg ${isVideoOff ? 'btn-danger' : 'btn-success'}`}
                                    onClick={toggleVideo}
                                >
                                    <FontAwesomeIcon icon={isVideoOff ? faVideoSlash : faVideo} className="me-2" />
                                    {isVideoOff ? 'Turn On Video' : 'Turn Off Video'}
                                </button>
                                <button 
                                    className={`btn btn-lg ${isRecording ? 'btn-danger' : 'btn-warning'}`}
                                    onClick={toggleRecording}
                                >
                                    <FontAwesomeIcon icon={isRecording ? faStop : faRecordVinyl} className="me-2" />
                                    {isRecording ? 'Stop Recording' : 'Start Recording'}
                                </button>
                                                                 <button 
                                     className="btn btn-lg btn-danger"
                                     onClick={() => {
                                         if (onSessionEnd) {
                                             onSessionEnd();
                                         } else if (onClose) {
                                             onClose();
                                         }
                                     }}
                                 >
                                     <FontAwesomeIcon icon={faPhoneSlash} className="me-2" />
                                     End Call
                                 </button>
                            </div>
                        </div>
                    </div>

                    {/* Participants */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faUsers} className="me-2" />
                                Participants ({session.participants.length})
                            </h6>
                        </div>
                        <div className="card-body">
                            {session.participants.map((participant, index) => (
                                <div key={index} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded">
                                    <div>
                                        <div className="fw-bold">{participant.name}</div>
                                        <small className="text-muted">{participant.type}</small>
                                    </div>
                                    <div className="d-flex gap-1">
                                        {participant.joined && (
                                            <span className="badge bg-success">
                                                <FontAwesomeIcon icon={faVideo} />
                                            </span>
                                        )}
                                        {participant.muted && (
                                            <span className="badge bg-warning">
                                                <FontAwesomeIcon icon={faMicrophoneSlash} />
                                            </span>
                                        )}
                                        {participant.video_off && (
                                            <span className="badge bg-secondary">
                                                <FontAwesomeIcon icon={faVideoSlash} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Chat */}
                    <div className="card">
                        <div className="card-header">
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faComments} className="me-2" />
                                Chat
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="chat-messages" style={{ height: '200px', overflowY: 'auto' }}>
                                {chatMessages.map((msg) => (
                                    <div key={msg.id} className="mb-2">
                                        <div className="d-flex justify-content-between">
                                            <small className="fw-bold">{msg.sender}</small>
                                            <small className="text-muted">
                                                {new Date(msg.timestamp).toLocaleTimeString()}
                                            </small>
                                        </div>
                                        <div className="p-2 bg-light rounded">
                                            {msg.message}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="input-group mt-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Type a message..."
                                    value={chatMessage}
                                    onChange={(e) => setChatMessage(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                                />
                                <button 
                                    className="btn btn-primary"
                                    onClick={sendChatMessage}
                                >
                                    <FontAwesomeIcon icon={faComments} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TelehealthInterface;
